import { useEffect } from 'react';
import { Bookmark } from 'react-bootstrap-icons';

import { Col, ColProps, Row, Select, Space, Typography } from 'antd';
import { Gutter } from 'antd/lib/grid/row';

import EmptyState from '@/components/shared/EmptyState';
import PageTitle from '@/components/shared/PageTitle';
import SkeletonBaseCard from '@/components/shared/SkeletonBaseCard';
import Wrapper from '@/components/shared/Wrapper';
import { useSocketShouldUpdateCourses } from '@/hooks/useSocketUpdateCourses';
import { useWindowScrollReachedEnd } from '@/hooks/useWindowScrollReachedEnd';
import { coursesApi } from '@/services/course';
import styled, { StyledProp } from '@/styled';

import Card from './Card';

const DEFAULT_ROWS_SPACING = [16, 16] as [Gutter, Gutter];

const DEFAULT_COLUMNS_WIDTH: Pick<ColProps, 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'> = {
  xs: 24,
  sm: 12,
  md: 12,
  lg: 8,
  xl: 6,
  xxl: 6
};

const MyCourses = (props: StyledProp) => {
  const update = useSocketShouldUpdateCourses();
  const reachedEnd = useWindowScrollReachedEnd();
  const [refetch, { data: response, isFetching }] = coursesApi.endpoints.getCourses.useLazyQuery();

  const handleOrder = (order: number) => {
    const orderMapper = [
      { order: 'ASC', orderBy: 'created_at' },
      { order: 'DESC', orderBy: 'created_at' },
      { order: 'ASC', orderBy: 'title' }
    ];

    refetch({ ...orderMapper[order], page: 1, size: 100 });
  };

  useEffect(() => {
    if (!response || isFetching || !reachedEnd) return;

    if (response.total_pages > response.page.page) {
      refetch({
        ...response.page,
        page: (response?.page.page ?? 0) + 1
      });
    }
  }, [reachedEnd, response, isFetching, refetch]);

  useEffect(() => {
    refetch({ page: 1, size: 100 });
  }, [update, refetch]);

  const orderByMenu = ['Mais recentes', 'Mais antigos', 'Alfabética A-Z'];
  const hasCourses = response?.data.length > 0;

  return (
    <Wrapper className={props.className}>
      <PageTitle
        title='Meus Cursos'
        endContent={
          <Space size={16}>
            <Select
              disabled={isFetching}
              onChange={handleOrder}
              placeholder={<Typography.Text strong>Exibir por</Typography.Text>}
              options={orderByMenu.map((label, value) => ({ value, label }))}
              size='large'
            />
          </Space>
        }
      />

      {isFetching && (
        <Row gutter={DEFAULT_ROWS_SPACING}>
          {new Array(8).fill('').map((_, index) => (
            <Col {...DEFAULT_COLUMNS_WIDTH} key={index}>
              <SkeletonBaseCard aspect='16:9' />
            </Col>
          ))}
        </Row>
      )}

      {!hasCourses && !isFetching && (
        <EmptyState
          icon={<Bookmark size={48} />}
          title='Seus cursos aparecerão aqui...'
          description='Aqui você poderá acessar com facilidade todos seus cursos'
        />
      )}

      {hasCourses && !isFetching && (
        <Row gutter={DEFAULT_ROWS_SPACING}>
          {response.data.map(course => (
            <Col {...DEFAULT_COLUMNS_WIDTH} key={course.id}>
              <Card {...course} />
            </Col>
          ))}
        </Row>
      )}
    </Wrapper>
  );
};

export default styled(MyCourses)`
  .ant-select {
    min-width: 150px;
  }
`;
